import { createContext, useContext, type ReactNode } from 'react'

interface RadioGroupContextValue {
  name: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null)

export interface RadioGroupProps {
  name: string
  value: string
  onChange: (value: string) => void
  children: ReactNode
  label?: string
  error?: string
  disabled?: boolean
  orientation?: 'horizontal' | 'vertical'
  className?: string
}

export function RadioGroup({
  name,
  value,
  onChange,
  children,
  label,
  error,
  disabled = false,
  orientation = 'vertical',
  className = '',
}: RadioGroupProps) {
  return (
    <RadioGroupContext.Provider value={{ name, value, onChange, disabled }}>
      <fieldset className={`flex flex-col gap-2 ${className}`}>
        {label && <legend className="text-sm font-medium text-gray-700 dark:text-white/65">{label}</legend>}
        <div
          className={`flex gap-4 ${orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap'}`}
          role="radiogroup"
        >
          {children}
        </div>
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
      </fieldset>
    </RadioGroupContext.Provider>
  )
}

export interface RadioProps {
  value: string
  label: string
  description?: string
  disabled?: boolean
  className?: string
}

export function Radio({ value, label, description, disabled: itemDisabled = false, className = '' }: RadioProps) {
  const context = useContext(RadioGroupContext)

  if (!context) {
    throw new Error('Radio must be used within a RadioGroup')
  }

  const { name, value: selectedValue, onChange, disabled: groupDisabled } = context
  const isDisabled = itemDisabled || groupDisabled
  const isChecked = selectedValue === value
  const inputId = `${name}-${value}`

  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <div className="flex h-5 items-center">
        <input
          type="radio"
          id={inputId}
          name={name}
          value={value}
          checked={isChecked}
          onChange={() => onChange(value)}
          disabled={isDisabled}
          className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:border-white/20 dark:focus:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>
      <div className="flex flex-col">
        <label
          htmlFor={inputId}
          className={`text-sm font-medium cursor-pointer select-none ${isDisabled ? 'text-gray-400 dark:text-white/30' : 'text-gray-700 dark:text-white/65'}`}
        >
          {label}
        </label>
        {description && <p className="text-sm text-gray-500 dark:text-white/45">{description}</p>}
      </div>
    </div>
  )
}
